import { SEOContentFAQ } from "../../../../components/seo/SEOContentFAQ";
import { SEOImage } from "../../../../components/seo/SEOImage";

export const OratoriaVideoPT = () => (
    <div className="text-slate-300">
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Oratória em Vídeo: 3 Pilares da Oratória Magnética
        </h2>

        <p className="lead text-xl text-slate-300 mb-8 font-medium">
            Você já sentiu aquele frio na barriga antes de gravar um vídeo? Ou se perdeu no roteiro, desviou o olhar da câmera e perdeu a conexão com sua audiência? No dinâmico mundo digital de hoje, a oratória em vídeo é essencial, mas dominar a fluidez e a naturalidade pode ser um desafio enorme.
        </p>

        <p className="text-slate-300 mb-8">
            <strong>Mas aqui está o segredo: oratória não é um dom, é uma habilidade treinada.</strong> Falar bem na frente das câmeras envolve alinhar sua mente, seu corpo e as ferramentas certas. Neste guia, vamos explorar os 3 pilares que transformam uma fala insegura em uma apresentação magnética, e como o <strong>PromptNinja</strong> é o parceiro secreto que te permite focar no que realmente importa: sua conexão com o público.
        </p>

        <SEOImage
            slug="dicas-oratoria-video"
            src="public-speaking-video-confident-youtuber.webp"
            alt="YouTuber confiante gravando com teleprompter"
            caption="Oratória magnética nasce da segurança; ferramentas como teleprompter liberam seu carisma natural."
            width={1200}
            height={675}
            priority={true}
        />

        {/* CTA Estratégico de Início */}
        <div className="bg-blue-600/10 border border-blue-500/20 rounded-2xl p-6 md:p-8 text-center mb-12">
            <h3 className="text-2xl font-bold text-white mb-4">Cansado de Gaguejar ou Esquecer o Roteiro?</h3>
            <p className="text-slate-300 mb-6">
                Milhares de criadores já transformaram sua forma de aparecer em vídeo.
                Experimente o <strong>PromptNinja</strong> e descubra a liberdade de se comunicar com naturalidade e profissionalismo.
            </p>
            <a href="https://promptninja.solutionkit.com.br/#app" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full transition-all hover:scale-105 shadow-lg shadow-blue-500/25">
                Comece Sua Jornada Grátis Agora!
            </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-white text-xl mb-3">1. Prepare seu conteúdo com antecedência e use o PromptNinja como seu guia inseparável</h3>
                <p className="text-sm">
                    A pedra angular de qualquer vídeo impactante é um roteiro bem elaborado. Não se trata apenas de listar tópicos, mas de construir uma narrativa fluida, que capture e mantenha a atenção. Defina com clareza a sua mensagem central e organize seus argumentos.
                </p>
                <div className="bg-slate-800 p-3 rounded-md mt-4 border-l-4 border-blue-500">
                    <p className="text-sm font-semibold text-white">O Toque Profissional</p>
                    <p className="text-xs text-slate-400">
                        Uma vez pronto, carregue-o no <strong>PromptNinja</strong>. Ele permite ler seu texto na tela com rolagem ajustável, garantindo que você nunca perca o fio da meada e foque 100% na entonação e na conexão real com a câmera.
                    </p>
                </div>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-white text-xl mb-3">2. O Fim dos Vícios de Linguagem</h3>
                <p className="text-sm">
                    "Ééé...", "tipo...", "né?". Esses preenchimentos surgem quando o cérebro busca a próxima palavra. Eles minam sua credibilidade e distraem o espectador. A fluidez verbal demonstra autoridade no assunto.
                </p>
                <div className="bg-slate-800 p-3 rounded-md mt-4 border-l-4 border-purple-500">
                    <p className="text-sm font-semibold text-white">A Solução: Roteiro Guiado</p>
                    <p className="text-xs text-slate-400">
                        Com o roteiro rolando suavemente na tela, seu cérebro não precisa "caçar" palavras. O <strong>PromptNinja</strong> garante que sua mensagem seja entregue de forma limpa e profissional.
                    </p>
                </div>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-white text-xl mb-3">3. A Regra da Energia +10%</h3>
                <p className="text-sm">
                    A câmera tende a "achatar" sua energia. O que parece normal ao vivo pode parecer monótono em vídeo. Projete sua voz, gesticule e sorria um pouco mais do que faria normalmente. Pense em elevar sua energia em 10 a 20%.
                </p>
                <div className="bg-slate-800 p-3 rounded-md mt-4 border-l-4 border-purple-500">
                    <p className="text-sm font-semibold text-white">A Solução: Foco na Performance</p>
                    <p className="text-xs text-slate-400">
                        Ao liberar sua mente da tarefa de memorizar, você pode se concentrar 100% na sua entrega, energia e linguagem corporal. O teleprompter cuida do texto.
                    </p>
                </div>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-white text-xl mb-3">4. O Poder da Pausa</h3>
                <p className="text-sm">
                    O silêncio é tão importante quanto a fala. Pausas estratégicas dão tempo para o público absorver a informação e criam antecipação. Não tenha pressa. Use as vírgulas e pontos do seu roteiro como momentos para respirar e deixar a mensagem brilhar.
                </p>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-white text-xl mb-3">5. Linguagem Corporal Aberta</h3>
                <p className="text-sm">
                    Cruzar os braços ou encolher os ombros transmite insegurança. Mantenha os ombros relaxados e abertos. Use as mãos para enfatizar pontos importantes, mas evite movimentos repetitivos que distraiam. Ocupar espaço transmite confiança.
                </p>
            </div>
        </div>

        {/* CTA Contextualizado */}
        <div className="bg-indigo-900/40 p-8 rounded-xl my-12 border-l-4 border-indigo-500 shadow-xl relative overflow-hidden group hover:bg-indigo-900/50 transition duration-300">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-indigo-500 rounded-full blur-3xl opacity-20 group-hover:opacity-30 transition duration-500"></div>

            <h3 className="font-bold text-2xl text-white mb-3 relative z-10">Chega de Travar: O Segredo da Fluidez</h3>
            <p className="text-slate-200 mb-6 relative z-10 max-w-2xl">
                As técnicas de oratória funcionam, mas se você ainda se sente inseguro ou perde o raciocínio, a melhor dica é: <strong>Use o PromptNinja</strong>. Ele permite que você leia com fluidez, eliminando a ansiedade, os brancos e a necessidade de decorar textos.
            </p>

            <a
                href="https://promptninja.solutionkit.com.br/#app"
                className="inline-block relative z-10 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:shadow-indigo-500/25 transform hover:-translate-y-1 transition duration-200"
            >
                Praticar com Teleprompter GRATUITO
            </a>
        </div>

        <div className="bg-slate-900 p-8 rounded-2xl border border-slate-800 shadow-lg mt-12">
            <h3 className="text-3xl font-bold text-white mb-6 text-center">O Inimigo Silencioso: Carga Cognitiva</h3>
            <p className="text-center max-w-2xl mx-auto">
                Tentar lembrar um roteiro, controlar a câmera, pensar na iluminação e ainda por cima parecer natural é uma receita para o desastre. Isso é chamado de alta carga cognitiva. É quando seu cérebro está sobrecarregado com múltiplas tarefas complexas.
            </p>
            <p className="text-center max-w-2xl mx-auto mt-4">
                A ferramenta mais eficaz para reduzir essa carga é o teleprompter. Ele remove a tarefa mais pesada – a memorização – permitindo que sua mente se concentre no que realmente importa: <strong>conectar-se com seu público.</strong>
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6 text-center">Como o PromptNinja Revoluciona sua Oratória</h3>
        <ul className="space-y-4 max-w-2xl mx-auto">
            <li className="bg-slate-800 p-4 rounded-lg border border-slate-700">
                <h3 className="font-bold text-white">Controle Total na Ponta dos Dedos</h3>
                <p className="text-sm text-slate-400">Use seu smartphone como um controle remoto seguro via QR Code. Pause, acelere ou desacelere o texto para combinar com seu ritmo natural de fala. Chega de parecer um robô lendo um texto.</p>
            </li>
            <li className="bg-slate-800 p-4 rounded-lg border border-slate-700">
                <h3 className="font-bold text-white">Funciona em Qualquer Dispositivo, Sem Instalar Nada</h3>
                <p className="text-sm text-slate-400">Abra o PromptNinja no navegador do seu PC, tablet ou celular. Sem downloads, sem complicações. Grave onde e como quiser.</p>
            </li>
            <li className="bg-slate-800 p-4 rounded-lg border border-slate-700">
                <h3 className="font-bold text-white">Fale e o Texto Acompanha (Recurso Pro)</h3>
                <p className="text-sm text-slate-400">Com nosso reconhecimento de voz, o roteiro avança conforme você fala. É a experiência de teleprompter mais fluida e profissional que existe.</p>
            </li>
        </ul>

        <SEOImage
            slug="dicas-oratoria-video"
            src="public-speaking-executive-video-conference.webp"
            alt="Executivo usando teleprompter em uma videoconferência"
            caption="O uso de teleprompter em videoconferências corporativas garante autoridade, clareza e contato visual constante."
            width={1200}
            height={675}
        />

        <div className="text-center mt-12">
            <a
                href="https://promptninja.solutionkit.com.br/#app"
                className="inline-block bg-gradient-to-r from-blue-500 to-purple-500 font-bold py-3 px-8 mx-auto rounded-full transition hover:brightness-110"
                style={{ color: 'white' }}
            >
                Destrave sua Confiança. Teste o PromptNinja de Graça!
            </a>
            <p className="text-slate-400 mt-4 text-sm">A ferramenta que separa os amadores dos profissionais.</p>
        </div>

        <div className="mt-16">
            <SEOContentFAQ
                title="Perguntas Frequentes (FAQ)"
                items={[
                    {
                        question: "Usar um teleprompter não vai me deixar com uma aparência robótica?",
                        answer: "A aparência robótica vem de tentar memorizar e recitar, não de ler com fluidez. É por isso que o controle remoto do PromptNinja é crucial, pois ele permite que você fale no seu ritmo natural, sem depender de uma rolagem automática forçada."
                    },
                    {
                        question: "Preciso de equipamento caro para usar o PromptNinja?",
                        answer: "Não. Você só precisa de dois dispositivos com acesso a um navegador: um para ser a tela (como um laptop ou tablet) e outro para ser o controle (seu celular). Não é necessário hardware adicional."
                    },
                    {
                        question: "É difícil de configurar?",
                        answer: "Leva menos de 30 segundos. Acesse o site, cole seu roteiro, clique em \"iniciar\" e escanea o QR code com seu celular. É a forma mais rápida e simples de ter um sistema de teleprompter profissional."
                    }
                ]}
            />
        </div>
    </div>
);
